import { get, isFunction, head, values, isString, isEmpty, every, find } from 'lodash';
import { from, Observable, of, timer } from 'rxjs';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import {
  FilterOptionType,
  filterTypeOptions,
  InitFilterParams,
  ListColumn,
  SelectOption,
  XuxGridAction,
} from '@xenial-ux/types';
import { debounce, map, startWith } from 'rxjs/operators';

const hasMethod = (subject: any, methodName: string): boolean => {
  return isFunction(get(subject, methodName));
};

export const isObservable = (subject: any): boolean => {
  return hasMethod(subject, 'subscribe');
};

export const asObservable = (src: Observable<any> | Promise<any> | any): Observable<any> => {
  if (hasMethod(src, 'then')) {
    return from(src);
  }
  return isObservable(src) ? (src as Observable<any>) : of(src);
};

/**
 * controlValueObs
 * Helper that SAFELY returns an observable for passed control values
 * STARTING from the current value of the control.
 * Default value can be passed for cases when control is undefined
 */
export const controlValueObs = (
  control: AbstractControl,
  defaultValue: any = null
): Observable<any> => {
  if (!get(control, 'valueChanges')) {
    return of(defaultValue);
  }
  return control.valueChanges.pipe(startWith(control.value));
};


export const controlInvalidObs = (
  control: AbstractControl, debounceTime = 1000, defaultValue = false): Observable<boolean> => {
  if (!get(control, 'statusChanges')) {
    return of(defaultValue);
  }
  return control.statusChanges.pipe(
    startWith(<string>null),
    map(() => control.invalid),
    debounce(invalid => timer(invalid ? debounceTime : 0))
  );
};

export const getFilterOptionsFromFilterParams = (params: InitFilterParams): SelectOption[] => {
  return params.filterOptions?.map((value: string) => {
    // TODO find more elegant solution of error in type
    // value param should be FilterOptionType type but in ag-grid filterOptions is string[]
    const filterOptionType = value as FilterOptionType;
    return find(filterTypeOptions, { value: filterOptionType });
  }) ?? [];
};

export const getTextFromControlErrors = (errors: ValidationErrors = {}): string => {
  if (!errors) {
    return null;
  }
  const errorToShow = head(values(errors));
  const erroMessage =
    isString(errorToShow) && !isEmpty(errorToShow) ? errorToShow : 'Validation Error';

  return erroMessage;
};

export const getIconTemplate = (iconId: string, className: string = ''): string => {
  return `<svg class="icon ${iconId} ${className}"><use xlink:href="#${iconId}"></use></svg>`;
};

export const isAlwaysVisiblePredicate = (listColumn: ListColumn): boolean => {
  return listColumn.defaultView || listColumn.hide === false;
};

export const detectMobile = (): boolean => {
  if (navigator.userAgent.match(/Android/i)
   || navigator.userAgent.match(/webOS/i)
   || navigator.userAgent.match(/iPhone/i)
   || navigator.userAgent.match(/iPad/i)
   || navigator.userAgent.match(/iPod/i)
   || navigator.userAgent.match(/BlackBerry/i)
   || navigator.userAgent.match(/Windows Phone/i)) { return true; }

  return false;
};

export const shouldEmitAction = async (action: XuxGridAction, validators: Function[]): Promise<boolean> => {
  const actionAcceptanceValidators = validators.filter(isFunction);

  if (isEmpty(actionAcceptanceValidators)) { return true; }

  const validatorResults = [];
  for (const validator of actionAcceptanceValidators) {
    const result = await validator(action);
    validatorResults.push(result);
  }

  return every(validatorResults, Boolean);
};
